/*
 * Purpose: Post-login plan selection — choose tier or continue as guest
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, CreditCard, UserRound } from 'lucide-react';
import { AuthCard } from '@/components/auth/ui/AuthUi';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { PRICING_PLANS, type PricingPlanId } from '@/lib/pricingPlans';
import { applyPlanSelection } from '@/lib/subscriptionState';
import { useAuthStore } from '@/stores/authStore';
import { APP_ROUTES } from '@/lib/appRoutes';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

interface PlanSelectionPanelProps {
  next?: string;
}

export function PlanSelectionPanel({ next = APP_ROUTES.dashboard }: PlanSelectionPanelProps) {
  const refreshSubscription = useAuthStore((s) => s.refreshSubscription);
  const [busy, setBusy] = useState<PricingPlanId | 'guest' | null>(null);

  async function selectPlan(tier: PricingPlanId) {
    setBusy(tier);
    try {
      const state = await applyPlanSelection({
        subscriptionTier: tier,
        subscriptionStatus: 'active',
        guestMode: false,
      });
      await refreshSubscription(state ?? undefined);
      toast.success(`${tier === 'plus' ? 'Plus' : 'Basic'} plan selected — you can save and run flows.`);
      window.location.href = next;
    } catch {
      toast.error('Could not save plan choice', {
        action: { label: 'Retry', onClick: () => void selectPlan(tier) },
      });
    } finally {
      setBusy(null);
    }
  }

  async function continueAsGuest() {
    setBusy('guest');
    try {
      const state = await applyPlanSelection({
        subscriptionTier: null,
        subscriptionStatus: 'guest',
        guestMode: true,
      });
      await refreshSubscription(state ?? undefined);
      toast.message('Guest mode — browse only. Saving and running flows are disabled.', {
        action: { label: 'Choose plan', onClick: () => { window.location.reload(); } },
      });
      window.location.href = next;
    } catch {
      toast.error('Could not enable guest mode', {
        action: { label: 'Retry', onClick: () => void continueAsGuest() },
      });
    } finally {
      setBusy(null);
    }
  }

  return (
    <AuthCard className="max-w-2xl w-full">
      <div className="text-center mb-4">
        <h1 className="text-xl font-semibold text-[var(--auth-fg)]">Choose your plan</h1>
        <p className="mt-1 text-sm text-[var(--auth-muted)]">
          Select Basic or Plus to save and run flows. Skip to browse as a guest — no saving or live runs.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {PRICING_PLANS.map((plan) => (
          <div
            key={plan.id}
            className={cn(
              'rounded-lg border p-4 flex flex-col gap-3',
              plan.highlighted
                ? 'border-[var(--auth-accent)]/50 bg-[var(--auth-accent)]/5'
                : 'border-[var(--auth-border)]'
            )}
          >
            <div className="flex items-start justify-between gap-2">
              <div>
                <h2 className="font-semibold text-[var(--auth-fg)]">{plan.name}</h2>
                <p className="text-xs text-[var(--auth-muted)] mt-0.5">{plan.tagline}</p>
              </div>
              {plan.highlighted ? (
                <Badge className="shrink-0 text-[10px]">Popular</Badge>
              ) : null}
            </div>
            <ul className="space-y-1.5 flex-1">
              {plan.features.slice(0, 4).map((feature) => (
                <li key={feature} className="flex gap-2 text-xs text-[var(--auth-muted)]">
                  <Check className="h-3.5 w-3.5 shrink-0 text-[var(--auth-accent)] mt-0.5" />
                  {feature}
                </li>
              ))}
            </ul>
            <Button
              actionTier={plan.highlighted ? 'primary' : 'secondary'}
              className="w-full"
              disabled={busy != null}
              onClick={() => void selectPlan(plan.id)}
            >
              {busy === plan.id ? 'Saving…' : `Choose ${plan.name}`}
            </Button>
          </div>
        ))}
      </div>

      <div className="mt-4 flex flex-col sm:flex-row gap-2">
        <Button variant="outline" className="flex-1" asChild>
          <Link to={`${APP_ROUTES.pricing}?next=${encodeURIComponent(next)}`}>
            <CreditCard className="h-4 w-4 mr-2" />
            Stripe checkout
          </Link>
        </Button>
        <Button
          actionTier="ghost"
          className="flex-1"
          disabled={busy != null}
          onClick={() => void continueAsGuest()}
        >
          <UserRound className="h-4 w-4 mr-2" />
          {busy === 'guest' ? 'Starting guest…' : 'Continue as guest (browse only)'}
        </Button>
      </div>
    </AuthCard>
  );
}

export default PlanSelectionPanel;
